import React, { FC, ReactElement } from 'react';
import cns from 'classnames';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import moment from 'moment';
import Icon from '../../../Icon';
import './Intro.sass';
import { getContentDataIntro, getIsLoggedIn, getIsRegistrationRequired, getExhibitionNavmenuButtons } from '../../../../stores/reducers/userData';
import routes from '../../../../config/routes';
import {ParamsDto, internalizationState} from '../../../../stores/reducers/sections';
import localeFieldResolver from '../../../../helpers/locale-field-finder';

interface Props {
  className?: string;
  sectionId: string;
  sectionData?: Array<ParamsDto>;
}

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent);

const Intro: FC<Props> = ({ className, sectionId, sectionData }): ReactElement => {
  const location = useLocation();
  const { backendURL, mainImage, decorationImage, timeStart, mainMobileImage, ...localeData } = useSelector(
    getContentDataIntro
  );
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isRegistrationRequired = useSelector(getIsRegistrationRequired);
  const showRegisterBtn = !isLoggedIn && isRegistrationRequired;

  const blockBackground = sectionData!.find((param) => param.type === 'introBlockBackground');
  const titleColor = sectionData!.find((param) => param.type === 'introTitleColor');
  const titleSize = sectionData!.find((param) => param.type === 'introTitleSize');

  const descriptionColor = sectionData!.find((param) => param.type === 'introDescriptionColor');
  const descriptionSize = sectionData!.find((param) => param.type === 'introDescriptionSize');

  const introDateColor = sectionData!.find((param) => param.type === 'introDateColor');
  const introDateSize = sectionData!.find((param) => param.type === 'introDateSize');
  const introDateBackground = sectionData!.find((param) => param.type === 'introDateBackground');

  const introTimeColor = sectionData!.find((param) => param.type === 'introTimeColor');
  const introTimeSize = sectionData!.find((param) => param.type === 'introTimeSize');
  const introTimeBackground = sectionData!.find((param) => param.type === 'introTimeBackground');

  const introStatusColor = sectionData!.find((param) => param.type === 'introStatusColor');
  const introStatusSize = sectionData!.find((param) => param.type === 'introStatusSize');
  const introStatusBackground = sectionData!.find((param) => param.type === 'introStatusBackground');

  const mainBtnBackground = sectionData!.find((param) => param.type === 'mainBtnBackground');
  const mainBtnText = sectionData!.find((param) => param.type === 'mainBtnText');
  const secondBtnBackground = sectionData!.find((param) => param.type === 'secondBtnBackground');
  const secondBtnText = sectionData!.find((param) => param.type === 'secondBtnText');

  const moreDetailsBtnParams = sectionData!.find((param) => param.type === 'moreDetailsBtn');

  const { activeLangState } = useSelector(internalizationState);
  const { exhibitionButtonLive } = useSelector(getExhibitionNavmenuButtons);
  const liveText = localeFieldResolver(exhibitionButtonLive, 'name', activeLangState);

  const localizeData = {
    registration_ru: 'Регистрация',
    registration_en: 'Registration',
    broadcast_ru: 'Трансляция',
    broadcast_en: 'Broadcast',
    more_details_ru: 'Подробнее',
    more_details_en: 'More details',
  };

  const localizedName = (field: string): string => localeFieldResolver(localizeData, field, activeLangState);
// eventName, description, description_en, eventName_en
  let css = '';
  if (mainImage) {
    css += `.main-section {
      background-image: url('${backendURL}${!isMobile ? mainImage : mainMobileImage}')
    }`;
  } else {
    css += `.main-section {
      background-color: ${blockBackground && blockBackground.value}
    }`
  }
  if (decorationImage) {
    css += `.shape-ptrn {
      background-image: url('${backendURL}${decorationImage}')
    }`;
  }

  let startMoment = null;
  if (timeStart) {
    startMoment = moment.unix(timeStart);
  }

  const utcString = (utc: number) => {
    if (activeLangState === 'ru') {
      let msk = utc - 3;
      if (msk > 12) {
        msk -= 24;
      } else if (msk < -12) {
        msk += 24;
      }
      if (Math.abs(msk) < 0.001) {
        return ` (MCK)`;
      }
      return ` (MCK${msk > 0 ? '+' : ''}${msk})`;
    }

    return ` (UTC${utc > 0 ? '+' : ''}${utc})`;
  };

  return (
    <>
      <style>{css}</style>
      <section className={cns('main-section', className)} id={sectionId}>
        <div className="shape-ptrn" />
        <div className="main-arrow">
          <Icon name="thin-arrow-down" />
        </div>
        <div className="container intro-container">
          {startMoment && (
            <div className="date-line">
              <span className="date-line__date" style={{
                  color: introDateColor && introDateColor.value,
                  fontSize: introDateSize && introDateSize.value + 'px',
                  backgroundColor: introDateBackground && introDateBackground.value
                }}
              >
                {startMoment.locale(activeLangState).format('DD MMMM')}
              </span>
              <span className="date-line__time" style={{
                  color: introTimeColor && introTimeColor.value,
                  fontSize: introTimeSize && introTimeSize.value + 'px',
                  backgroundColor: introTimeBackground && introTimeBackground.value
                }}
              >
                {startMoment.format('HH:mm')}
                {utcString(startMoment.utcOffset() / 60)}
              </span>
              <span className="date-line__status" style={{
                  color: introStatusColor && introStatusColor.value,
                  fontSize: introStatusSize && introStatusSize.value + 'px',
                  backgroundColor: introStatusBackground && introStatusBackground.value
                }}
              >
                Online
              </span>
            </div>
          )}
          <h1
            className="intro__title"
            style={{
              color: titleColor && titleColor.value,
              fontSize: titleSize && titleSize.value + 'px'
            }}
          >
            {localeFieldResolver(localeData, 'eventName', activeLangState)}
          </h1>
          <p
            className="user-fs-text intro__description"
            style={{
              color: descriptionColor && descriptionColor.value,
              fontSize: descriptionSize && descriptionSize.value + 'px'
            }}
          >
            {localeFieldResolver(localeData, 'description', activeLangState)}
          </p>
          {showRegisterBtn ? (
            <Link
              className="btn btn-primary btn--mobile-min"
              to={routes.REGISTER}
              style={{
                backgroundColor: mainBtnBackground && mainBtnBackground.value,
                color: mainBtnText && mainBtnText.value
              }}
            >
              {localizedName('registration_ru')}
            </Link>
          ) : (
            <Link
              className="btn btn-primary btn--mobile-min"
              to={routes.LIVE}
              style={{
                backgroundColor: mainBtnBackground && mainBtnBackground.value,
                color: mainBtnText && mainBtnText.value
              }}
            >
              {liveText || localizedName('broadcast_ru')}
            </Link>
          )}

          {
            moreDetailsBtnParams?.linkType === 'self' ? (
              <ScrollLink
                className="btn btn--text btn--mobile-min btn--light-color"
                          to={moreDetailsBtnParams?.value ?? ''}
                smooth
                offset={-164}
                duration={500}
                style={
                  {
                    backgroundColor: secondBtnBackground && secondBtnBackground.value,
                    color: secondBtnText && secondBtnText.value,
                    display: moreDetailsBtnParams?.hidden ? 'none' : 'inline-block'
                  }
                }
              >
                {localizedName('more_details_ru')}
              </ScrollLink>
            )
            : (
              <a
                className="btn btn--text btn--mobile-min btn--light-color"
                href={moreDetailsBtnParams?.value ?? ''}
                target={moreDetailsBtnParams?.blank ? '_blank' : ''}
                style={
                  {
                    backgroundColor: secondBtnBackground && secondBtnBackground.value,
                    color: secondBtnText && secondBtnText.value,
                    display: moreDetailsBtnParams?.hidden ? 'none' : 'inline-block'
                  }
                }
              >
                {localizedName('more_details_ru')}
              </a>
            )
          }
        </div>
      </section>
    </>
  );
};

export default Intro;
